import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContractStatus } from '@bhops/shared';
import { productionPlansApi, type ProductionPlan } from '@/api/production-plans';
import { contractsApi } from '@/api/contracts';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { kstYmd } from '@/lib/format-date';

// docs/work_log/p3-plus-production-plan-allocate — windows audit (P3+) 에서
// "ProductionPlan ↔ Contract allocate UI 부재 — backend endpoint 존재,
// 화면 미구현" 으로 드러난 갭 해결.
//
// 모달 구성:
//   1) 상단 — 현재 할당 목록 + 각 row 에 release 버튼
//   2) 하단 — 신규 할당 폼:
//        • 수량 (1 ~ availableCount)
//        • 계약 picker (status: ACTIVE)
//        • 메모 (선택)
//
// Backend:
//   POST /production-plans/:planId/allocate    body: { allocatedCount, contractId?, dealId?, notes? }
//   POST /production-plans/allocations/release body: { allocationId, releaseReason? }
//
// FB3 §10 — 계약(CONTRACT) 전용. ACTIVE 계약에 plan 의 일부 수량을 pre-allocate
//   해 인보이스/SLA 시작 후 실 로봇 입고 시 link. (이전의 "딜(사전 예약)" 모드는
//   운영에서 미사용이라 제거 — 백엔드 allocate 는 dealId 를 계속 수용하지만 UI 는
//   더 이상 보내지 않는다.) 기존 할당 목록 row 는 과거 deal 할당도 안전하게
//   표시하도록 contract/deal humanId fallback 을 유지한다.

interface Props {
  open: boolean;
  onClose: () => void;
  plan: ProductionPlan;
}

export function ProductionPlanAllocateModal({ open, onClose, plan }: Props) {
  const toast = useToast();
  const qc = useQueryClient();

  // FB3 §10 — 계약(contract) 전용. deal 모드 제거.
  const [count, setCount] = useState<number>(1);
  const [contractId, setContractId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // release 확인 다이얼로그
  const [releaseTarget, setReleaseTarget] = useState<{ id: string; label: string } | null>(null);

  // 모달이 열릴 때마다 폼 초기화. availableCount 가 0 이면 폼은 비활성.
  //
  // IMPORTANT: deps must NOT include `plan.availableCount`. Parent refetches
  // (e.g. after another allocation lands) bump availableCount and would wipe
  // the operator's in-progress count/contractId/notes mid-input. The form's
  // max constraint validates on submit; silently resetting is worse UX than
  // letting submission fail loud.
  // Also reset `releaseTarget` so a stale ConfirmDialog never re-opens with
  // a previous-session allocation id after the parent modal is re-opened.
  //
  // F7 follow-up: `plan.id` IS in deps so that if the parent swaps the plan
  // prop to a different ProductionPlan while the modal stays open (e.g. user
  // opens modal for plan A, then a list-level action navigates to plan B
  // and the same modal instance is reused), the form resets to defaults for
  // the new plan instead of submitting stale contract/count against the
  // wrong plan id.
  useEffect(() => {
    if (!open) return;
    setCount(Math.min(1, plan.availableCount ?? 0));
    setContractId('');
    setNotes('');
    setError(null);
    setReleaseTarget(null);
  }, [open, plan.id]);

  // 활성 계약 목록 — DRAFT 는 아직 sign 안 된 상태라 allocation 대상 X.
  // EXPIRED/TERMINATED 는 종료된 계약이라 의미 없음.
  const contractsQ = useQuery({
    queryKey: ['contracts', 'allocate-picker', { status: ContractStatus.ACTIVE }],
    queryFn: () => contractsApi.list({ status: ContractStatus.ACTIVE, pageSize: 100 }),
    enabled: open,
    staleTime: 30_000,
  });

  const allocate = useMutation({
    mutationFn: () =>
      productionPlansApi.allocate(plan.id, {
        allocatedCount: count,
        contractId,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-plans'] });
      qc.invalidateQueries({ queryKey: ['assignments'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'manufacturing'] });
      toast.success(`${plan.humanId}에 ${count}대를 할당했습니다.`);
      // 본 할당으로 가용 수량이 모두 소진된 경우, 모달을 자동 닫아 운영자가
      // "끝났다" 는 신호를 명확히 받도록. 그 외에는 폼만 초기화하고 모달은
      // 유지 — 같은 plan 에 여러 allocation 흐름 지원.
      if ((plan.availableCount ?? 0) - count <= 0) {
        onClose();
      } else {
        setCount(1);
        setContractId('');
        setNotes('');
        setError(null);
      }
    },
    onError: (err) => setError(extractApiError(err)),
  });

  const release = useMutation({
    mutationFn: (allocationId: string) => productionPlansApi.release(allocationId, '운영자 해제'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-plans'] });
      qc.invalidateQueries({ queryKey: ['assignments'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'manufacturing'] });
      toast.success('할당을 해제했습니다.');
      setReleaseTarget(null);
    },
    onError: (err) => toast.error(extractApiError(err), '해제 실패'),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (count < 1) {
      setError('할당 수량은 1 이상이어야 합니다.');
      return;
    }
    if (count > (plan.availableCount ?? 0)) {
      setError(`가용 수량 ${plan.availableCount ?? 0}대를 넘을 수 없습니다.`);
      return;
    }
    if (!contractId) {
      setError('계약을 선택해 주세요.');
      return;
    }
    allocate.mutate();
  }

  const available = plan.availableCount ?? 0;
  const allocations = plan.allocations ?? [];
  const formDisabled = available <= 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${plan.humanId} 할당 관리`}
      width={560}
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          닫기
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* 현황 박스 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
            padding: 10,
            background: 'var(--bg-sunken)',
            borderRadius: 'var(--r-card)',
            fontSize: 12,
          }}
        >
          <div>
            <div style={{ color: 'var(--fg-muted)' }}>계획 수량</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>{plan.plannedCount}</div>
          </div>
          <div>
            <div style={{ color: 'var(--fg-muted)' }}>이미 할당</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>
              {plan.allocatedCount ?? 0}
            </div>
          </div>
          <div>
            <div style={{ color: 'var(--fg-muted)' }}>가용</div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                marginTop: 2,
                color: available > 0 ? 'var(--robot-operating)' : 'var(--fg-muted)',
              }}
            >
              {available}
            </div>
          </div>
        </div>

        {/* 기존 할당 목록 */}
        <section>
          <div className="kuun-label" style={{ marginBottom: 6 }}>
            현재 할당 ({allocations.length})
          </div>
          {allocations.length === 0 ? (
            <div
              style={{
                padding: 12,
                textAlign: 'center',
                color: 'var(--fg-muted)',
                fontSize: 12,
                border: '1px dashed var(--border-default)',
                borderRadius: 'var(--r-card)',
              }}
            >
              아직 할당이 없습니다. 아래에서 신규 할당하세요.
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 12,
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-card)',
              }}
            >
              <thead>
                <tr style={{ background: 'var(--bg-sunken)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px' }}>대상</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px' }}>수량</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px' }}>할당일</th>
                  <th style={{ padding: '6px 10px' }}></th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((a) => (
                  <tr key={a.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                      {a.contract?.humanId ?? a.deal?.humanId ?? '(unknown)'}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{a.allocatedCount}</td>
                    <td
                      style={{
                        padding: '6px 10px',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--fg-muted)',
                      }}
                    >
                      {/* 배정 시각은 `ProductionPlanAllocation.allocatedAt @default(now())`
                          — 화면이 못 정하는 **진짜 순간**이다. 한국 오전 0~9시 배정은
                          UTC 자르기에서 어제로 찍혔다. */}
                      {kstYmd(a.allocatedAt) ?? '-'}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() =>
                          setReleaseTarget({
                            id: a.id,
                            label: a.contract?.humanId ?? a.deal?.humanId ?? a.id,
                          })
                        }
                        disabled={release.isPending}
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          border: '1px solid var(--border-default)',
                          background: 'var(--bg-surface)',
                          borderRadius: 'var(--r-btn)',
                          cursor: 'pointer',
                        }}
                      >
                        해제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* 신규 할당 폼 */}
        <section>
          <div className="kuun-label" style={{ marginBottom: 6 }}>
            신규 할당
          </div>
          <form onSubmit={submit} noValidate>
            <FormError>{error}</FormError>

            {formDisabled && (
              <div
                style={{
                  padding: 8,
                  fontSize: 12,
                  background: 'var(--bg-info-subtle)',
                  border: '1px solid var(--border-info-saturated)',
                  borderRadius: 'var(--r-card)',
                  color: 'var(--fg-on-info-tint)',
                  marginBottom: 12,
                }}
              >
                이 계획에는 가용 수량이 없습니다. 기존 할당을 해제하거나 새 계획을 만들어 주세요.
              </div>
            )}

            {/* FB3 §10 — 계약 전용. 모드 토글 제거, 계약 picker 단독 노출. */}
            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 12 }}>
              <Field label="수량" htmlFor="alloc-count" required>
                <Input
                  id="alloc-count"
                  type="number"
                  min={1}
                  max={available}
                  value={count}
                  onChange={(e) => setCount(Number(e.target.value))}
                  disabled={formDisabled}
                  required
                />
              </Field>
              <Field label="대상 계약" htmlFor="alloc-contract" required>
                <Select
                  id="alloc-contract"
                  value={contractId}
                  onChange={(e) => setContractId(e.target.value)}
                  disabled={formDisabled || contractsQ.isLoading}
                  required
                >
                  <option value="">{contractsQ.isLoading ? '불러오는 중…' : '계약 선택…'}</option>
                  {(contractsQ.data?.items ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.humanId} · {c.customer?.displayName ?? '고객사 없음'}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="메모" htmlFor="alloc-notes" hint="비워 둬도 됩니다.">
              <Input
                id="alloc-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={formDisabled}
              />
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" disabled={formDisabled || allocate.isPending || !contractId}>
                {allocate.isPending ? '할당 중…' : '할당'}
              </Button>
            </div>
          </form>
        </section>
      </div>

      {/* 할당 해제 확인 다이얼로그 — window.confirm 대체 */}
      <ConfirmDialog
        open={!!releaseTarget}
        title="할당 해제 확인"
        message={`${releaseTarget?.label ?? ''} 할당을 해제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`}
        confirmLabel="해제"
        danger
        busy={release.isPending}
        onConfirm={() => {
          if (releaseTarget) release.mutate(releaseTarget.id);
        }}
        onCancel={() => {
          if (!release.isPending) setReleaseTarget(null);
        }}
      />
    </Modal>
  );
}
